import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { calculators } from "@/mocks/calculators";
import { Calculator, FileText, ArrowRight } from "lucide-react";

export default function CalculatorsHubPage() {
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/calculators`;

  const schemas = [
    webpageSchema(
      "Financial Calculators | SIP, Health, Term, Retirement | ExpertAdvise.in",
      "Free financial calculators for SIP, health insurance premium, term insurance, retirement corpus, child education, and human life value. Get detailed PDF reports.",
      pageUrl
    ),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Calculators", url: pageUrl },
    ]),
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Financial Calculators | SIP, Health, Term, Retirement | ExpertAdvise.in"
        description="Free financial calculators for SIP, health insurance premium, term insurance, retirement corpus, child education, and human life value. Get detailed PDF reports."
        keywords="SIP calculator, health insurance calculator, term insurance calculator, retirement calculator, child education calculator, HLV calculator"
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Breadcrumbs
              items={[
                { name: "Home", href: "/" },
                { name: "Calculators" },
              ]}
              className="justify-center mb-4"
            />
            <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
              Financial Tools
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">
              Smart Financial Calculators
            </h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
              Plan your insurance coverage, investment returns, and retirement corpus with our 
              easy-to-use, accurate financial calculators. Download detailed PDF reports for your records.
            </p>
          </div>
        </div>
      </section>

      {/* Calculators Grid */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {calculators.map((calc) => (
              <Link
                key={calc.id}
                to={calc.route}
                className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-primary-200 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl ${calc.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <i className={`${calc.icon} text-2xl`}></i>
                  </div>
                  {calc.badge && (
                    <span className="bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
                      {calc.badge}
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-primary-700 transition-colors">
                  {calc.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-4">
                  {calc.description}
                </p>
                <div className="flex items-center gap-1 text-sm font-semibold text-primary-600 group-hover:text-primary-700 transition-colors">
                  <Calculator className="w-4 h-4" />
                  <span>Use Calculator</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>

          {/* Info Banner */}
          <div className="mt-12 bg-white rounded-2xl border border-slate-200 p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
            <div className="w-14 h-14 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
              <FileText className="w-7 h-7 text-primary-600" />
            </div>
            <div className="text-center md:text-left">
              <h3 className="text-lg font-bold text-slate-900 mb-1">Download Detailed PDF Reports</h3>
              <p className="text-sm text-slate-500">
                Every calculator generates a comprehensive PDF report with year-wise projections, 
                charts, and actionable recommendations that you can share with your advisor or family.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">
            Need Personalized Advice?
          </h2>
          <p className="text-slate-600 mb-6">
            Calculators give estimates. Our advisors give you certainty. Book a free consultation today.
          </p>
          <Link
            to="/book-appointment?source=Calculators%20Page"
            className="inline-block bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap"
          >
            Book Free Consultation
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}